import { LOW_CONFIDENCE_ATTEMPTS } from '@/constants/app';
import type { Attempt, AttemptRollup } from './types';

export type Trend = 'up' | 'down' | 'flat' | 'unknown';

export interface CategoryStat {
  category: string;
  attempts: number;
  correct: number;
  /** 0..1, or null when there is nothing to divide. */
  accuracy: number | null;
  /** Fewer than five attempts: the number means little yet. */
  lowConfidence: boolean;
  trend: Trend;
}

const TREND_WINDOW = 10;
const TREND_MARGIN = 0.1;

/**
 * Accuracy per category, built from raw attempts plus the rolled-up totals that
 * replaced attempts older than ninety days.
 */
export function categoryStats(attempts: Attempt[], rollups: AttemptRollup[]): CategoryStat[] {
  const live = attempts.filter((a) => !a.deleted).sort((a, b) => a.at - b.at);
  const totals = new Map<string, { attempts: number; correct: number }>();

  const add = (category: string, n: number, correct: number) => {
    const entry = totals.get(category) ?? { attempts: 0, correct: 0 };
    entry.attempts += n;
    entry.correct += correct;
    totals.set(category, entry);
  };

  for (const rollup of rollups) {
    if (rollup.deleted) continue;
    add(rollup.category, rollup.attempts, rollup.correct);
  }
  for (const attempt of live) add(attempt.category, 1, attempt.correct ? 1 : 0);

  const byCategory = new Map<string, Attempt[]>();
  for (const attempt of live) {
    const list = byCategory.get(attempt.category) ?? [];
    list.push(attempt);
    byCategory.set(attempt.category, list);
  }

  return [...totals.entries()]
    .map(([category, total]) => ({
      category,
      attempts: total.attempts,
      correct: total.correct,
      accuracy: total.attempts ? total.correct / total.attempts : null,
      lowConfidence: total.attempts < LOW_CONFIDENCE_ATTEMPTS,
      trend: trendFor(byCategory.get(category) ?? []),
    }))
    .sort((a, b) => (a.accuracy ?? 1) - (b.accuracy ?? 1));
}

/** Compares the last ten answers with the ten before them. */
export function trendFor(attempts: Attempt[]): Trend {
  if (attempts.length < TREND_WINDOW + 2) return 'unknown';
  const recent = attempts.slice(-TREND_WINDOW);
  const previous = attempts.slice(-TREND_WINDOW * 2, -TREND_WINDOW);
  if (previous.length === 0) return 'unknown';

  const rate = (rows: Attempt[]) => rows.filter((a) => a.correct).length / rows.length;
  const delta = rate(recent) - rate(previous);
  if (delta > TREND_MARGIN) return 'up';
  if (delta < -TREND_MARGIN) return 'down';
  return 'flat';
}

/** The five worst topics, ignoring anything with too little data to judge. */
export function worstTopics(stats: CategoryStat[], limit = 5): CategoryStat[] {
  const confident = stats.filter((s) => !s.lowConfidence && s.accuracy !== null);
  const pool = confident.length > 0 ? confident : stats.filter((s) => s.accuracy !== null);
  return pool.slice(0, limit);
}
